import { useState, useEffect } from "react"
import { GoogleMap, Marker, Circle, Polygon } from "@react-google-maps/api"
import CustomMarkerOverlay from "../CustomMarkerOverlay"
import { isValidImageURL } from "../../lib/media"
import { useDecision } from "@optimizely/react-sdk"
import theme from "../../themes"

export const MapDisplay = (props) => {
    const [decisionCustomPins] = useDecision('custom-pins')
    const [decisionGamezone] = useDecision('gamezone')
    const { 
        center = { lat: 43.6426, lng: -79.3871 }, 
        withMarker, 
        markerPath, 
        renderPath, // always rendered regardless of experiment flag, lower priority than markerPath
        renderIcon, // icon object supplied directly, highest priority
        markerProps = {},
        radius, // radius in meters
        zones = [], // array of zones with waypoints to render
        markerFillColor = theme.primaryColor,
        radiusFillColor = theme.primaryColor,
        radiusStrokeColor = theme.primaryColor,
        ...mapProps 
    } = props
    const [markerIcon, setMarkerIcon] = useState(undefined)

    useEffect(() => {
        // Determine which icon path / object to use based on precedence:
        // 1. renderIcon (object) if provided
        // 2. markerPath (string) if experiment enabled
        // 3. renderPath (string) fallback (always rendered)

        if (renderIcon && renderIcon.url) {
            // Directly use provided icon object
            setMarkerIcon(renderIcon)
            return
        }

        let pathToUse = null
        if (markerPath && decisionCustomPins?.enabled) {
            pathToUse = markerPath
        } else if (renderPath) {
            pathToUse = renderPath
        }

        if (!pathToUse) {
            setMarkerIcon(undefined)
            return
        }

        isValidImageURL(
            pathToUse,
            valid => {
                if (valid && window?.google?.maps) {
                    setMarkerIcon({
                        url: pathToUse,
                        scaledSize: new window.google.maps.Size(26, 32),
                    })
                } else {
                    setMarkerIcon(undefined)
                }
            }
        )
    }, [markerPath, renderPath, renderIcon, decisionCustomPins?.enabled])

    return (
        <GoogleMap
            mapContainerStyle={{ width: "100%", height: "300px" }}
            center={center}
            zoom={15}
            {...mapProps}
        >
            {withMarker && (
                markerProps?.draggable ? (
                    <Marker
                        position={center}
                        icon={markerIcon}
                        {...markerProps}
                    />
                ) : (
                    <CustomMarkerOverlay
                        position={center}
                        icon={markerIcon}
                        isDynamicIcon={false}
                        fillColor={markerFillColor}
                    />
                )
            )}
            {radius > 0 && (
                <Circle
                    center={center}
                    radius={radius} // Google Maps uses meters for radius
                    options={{
                        strokeColor: radiusStrokeColor,
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: radiusFillColor,
                        fillOpacity: 0.35,
                        zIndex: 1,
                    }}
                />
            )}
            {decisionGamezone?.enabled && zones?.length > 0 && zones.map((zone, idx) => (
                zone?.waypoints?.length > 0 && (
                    <Polygon
                        key={zone.id || idx}
                        paths={zone.waypoints.map(wp => ({ lat: wp.latitude, lng: wp.longitude }))}
                        options={{
                            strokeColor: theme.accent2Color,
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: theme.accent2Color,
                            fillOpacity: 0.35,
                            zIndex: 0,
                            clickable: false,
                            draggable: false,
                            editable: false,
                        }}
                    />
                )
            ))}
        </GoogleMap>
    )
}

export default MapDisplay
